import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const NavigationContext = createContext({
  prevPath: null,
  currentPath: '/',
  direction: 0,
  transitionType: 'fade',
  zoomOriginRef: { current: null },
  setZoomOrigin: () => {},
});

const ROUTE_ORDER = {
  '/history': 0,
  '/': 1,
  '/projects': 1,
  '/skills': 2,
  '/travel': 3,
};

const PROJECT_DETAIL = /^\/projects\/[^/]+$/i;

const orderOf = (path) => {
  const lower = path.toLowerCase();
  if (lower in ROUTE_ORDER) return ROUTE_ORDER[lower];
  if (PROJECT_DETAIL.test(lower)) return 1.5;
  return 1;
};

const isProjectsHome = (path) => {
  const p = path.toLowerCase();
  return p === '/' || p === '/projects';
};

const isProjectDetail = (path) => PROJECT_DETAIL.test(path.toLowerCase());

const computeTransition = (prev, curr) => {
  if (!prev) return { type: 'fade', direction: 0 };
  if (isProjectsHome(prev) && isProjectDetail(curr)) {
    return { type: 'zoom-in', direction: 0 };
  }
  if (isProjectDetail(prev) && isProjectsHome(curr)) {
    return { type: 'zoom-out', direction: 0 };
  }
  const a = orderOf(prev);
  const b = orderOf(curr);
  if (a === b) return { type: 'fade', direction: 0 };
  return { type: 'slide', direction: b > a ? 1 : -1 };
};

export function NavigationProvider({ children }) {
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [prevPath, setPrevPath] = useState(null);
  const [meta, setMeta] = useState({ type: 'fade', direction: 0 });
  const zoomOriginRef = useRef(null);

  // Compute the transition SYNCHRONOUSLY during render — not in useEffect —
  // so the new page's PageTransition reads the correct variant on its first
  // commit. Doing this in useEffect leaves the previous nav's transitionType
  // in context for one render, which causes the wrong exit/entry variants
  // (e.g. project-detail's zoom-in blur leaking onto the Skills entry).
  if (location.pathname !== currentPath) {
    const t = computeTransition(currentPath, location.pathname);
    setPrevPath(currentPath);
    setCurrentPath(location.pathname);
    setMeta(t);
  }

  // Side effects (blossom-zoom dispatch) still belong in useEffect — they
  // touch the window and shouldn't run during render.
  useEffect(() => {
    if (!prevPath) return;
    if (meta.type === 'slide') {
      window.dispatchEvent(new CustomEvent('blossom-zoom', {
        detail: { phase: 'slide', x: window.innerWidth / 2, y: window.innerHeight / 2, dir: meta.direction },
      }));
    } else if (meta.type === 'fade') {
      window.dispatchEvent(new CustomEvent('blossom-zoom', {
        detail: { phase: 'in', x: window.innerWidth / 2, y: window.innerHeight / 2 },
      }));
    }
    // currentPath captures the latest navigation; meta is set in the same
    // batch, so a single dependency is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const setZoomOrigin = (xy) => {
    zoomOriginRef.current = xy;
  };

  return (
    <NavigationContext.Provider
      value={{
        prevPath,
        currentPath,
        direction: meta.direction,
        transitionType: meta.type,
        zoomOriginRef,
        setZoomOrigin,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export const useNavigationMeta = () => useContext(NavigationContext);
